<template>
    <vx-expandable-card>
        <template v-slot:header> What </template>
        <template v-slot:content>
            <strong class="text-slate-900 text-sm font-semibold mb-2 block sm:text-base"> Message </strong>
            <input-with-errors :errors="props.errors" :type="errorsAlertType(props.errors)">
                <template v-slot:alert="{ error }">
                    <quick-blast-alert :error="error" />
                </template>

                <message-builder
                    ref="messageBuilder"
                    v-model="message.body"
                    v-model:mediaUrl="message.media_url"
                    v-model:mediaContentType="message.media_content_type"
                    v-model:snippetId="message.snippet_id"
                    v-model:snippetFieldBindings="message.field_bindings"
                    :initialSnippet="message.snippet"
                    @selected:snippet="emit('snippetUsed', $event)"
                    :error="displayFirstError(validate.body.$errors)"
                    :blockImages="blockImages"
                    :blockFiles="blockFiles"
                    :onlyLockedSnippetsAllowed="onlyLockedSnippetsAllowed"
                    :disableLockedSnippets="props.campaignType === AUDIENCE_TYPE_TRANSACTIONAL"
                    preview
                    :enableAi="
                        FEATURES.quick_blast_ai && !FEATURES.quick_blast_cost_optimization && !onlyLockedSnippetsAllowed
                    "
                    @click:ai="aiModal = true"
                >
                    <template
                        v-if="
                            FEATURES.quick_blast_ai &&
                            FEATURES.quick_blast_cost_optimization &&
                            !onlyLockedSnippetsAllowed
                        "
                        v-slot:actionsAppend
                    >
                        <quick-blast-ai-action @click="aiModal = true"></quick-blast-ai-action>
                    </template>
                </message-builder>
            </input-with-errors>

            <message-builder-quick-blast-generation
                v-if="FEATURES.quick_blast_ai && !onlyLockedSnippetsAllowed"
                v-model:visible="aiModal"
                :campaign-type="props.campaignType"
                :message-body="message.body"
                @selected="message.body = $event"
            ></message-builder-quick-blast-generation>
        </template>
    </vx-expandable-card>
</template>

<script setup>
import { VxExpandableCard } from '@voxie/frontend-components';
import { inject, ref, computed } from 'vue';
import MessageBuilder from '~/components/general/message-builder/MessageBuilder.vue';
import QuickBlastAlert from '~/components/quick-blast/QuickBlastAlert.vue';
import QuickBlastAiAction from '~/components/quick-blast/QuickBlastAiAction.vue';
import MessageBuilderQuickBlastGeneration from '~/components/general/message-builder/modals/MessageBuilderQuickBlastGeneration.vue';
import { AUDIENCE_TYPE_MARKETING, AUDIENCE_TYPE_TRANSACTIONAL, errorsAlertType } from '~/constants/campaign';
import InputWithErrors from '~/components/general/InputWithErrors.vue';
import useVuelidate from '@vuelidate/core';
import { helpers, required } from '@vuelidate/validators';
import { displayFirstError } from '~/utils/validation';
import { useMessageRules } from '~/components/quick-blast/useMessageRules';

const FEATURES = inject('FEATURES');

const { shouldBlockImages, shouldBlockFiles, lockedSnippetsEnabled } = useMessageRules();

const props = defineProps({
    campaignType: {
        type: String,
        required: true,
        validator: (campaignType) => [AUDIENCE_TYPE_MARKETING, AUDIENCE_TYPE_TRANSACTIONAL].includes(campaignType),
    },
    errors: {
        type: Array,
        default: () => [],
    },
});

const emit = defineEmits(['snippetUsed']);

const message = defineModel({ type: Object, required: true, default: () => ({}) });

const aiModal = ref(false);

const blockImages = computed(() => shouldBlockImages(props.campaignType));
const blockFiles = computed(() => shouldBlockFiles(props.campaignType));
const onlyLockedSnippetsAllowed = computed(() => lockedSnippetsEnabled(props.campaignType));

const validate = useVuelidate(
    {
        body: {
            required: helpers.withMessage('Message is required', required),
        },
    },
    message
);
</script>
